import React from 'react';
import './VerificationLog.css';

const VerificationLog = ({ sessions, linkId }) => {
  if (!sessions || sessions.length === 0) return null;

  const formatTimestamp = (timestamp) => {
    const date = new Date(timestamp);
    return date.toLocaleString('en-AU', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: false
    });
  };

  const calculateCompletionTime = () => {
    if (sessions.length < 2) return '0s';
    const start = new Date(sessions[0].timestamp);
    const end = new Date(sessions[sessions.length - 1].timestamp);
    const diffSeconds = Math.round((end - start) / 1000);
    return diffSeconds + 's';
  };

  const checkSameIP = () => {
    if (sessions.length < 2) return true;
    const firstIP = sessions[0].network?.ip;
    return sessions.every(s => s.network?.ip === firstIP);
  };

  const getLocation = () => {
    const lastSession = sessions[sessions.length - 1];
    if (!lastSession.network) return 'Unknown';
    return `${lastSession.network.city}, ${lastSession.network.country}`;
  };

  return (
    <div className="verification-log">
      <div className="log-header">
        <h2>Identity Verification Log</h2>
        <div className="log-meta">
          <span className="link-id">Link ID: {linkId || '1377'} | {sessions[0].device.type === 'Mobile' ? 'Mobile User' : 'Desktop User'}</span>
          <span className="log-stats">{sessions.length} Attempt{sessions.length > 1 ? 's' : ''} | {formatTimestamp(sessions[sessions.length - 1].timestamp).split(' at ')[0]}</span>
        </div>
        <div className="verification-status">
          <span className="status-badge success">✓ Verification Complete</span>
        </div>
      </div>

      <div className="log-timeline">
        {sessions.map((session, index) => (
          <div key={index} className={`session-entry ${session.completed ? 'completed' : ''}`}>
            <div className="session-number">{index + 1}</div>
            
            <div className="session-content">
              <div className="session-header">
                <h3>{index === 0 ? 'Desktop Access - Page Load' : 'Mobile Verification Complete'}</h3>
                <span className="session-time">{formatTimestamp(session.timestamp)}</span>
                {index > 0 && (
                  <span className="time-diff">
                    ({Math.round((new Date(session.timestamp) - new Date(sessions[0].timestamp)) / 1000)} seconds after page load)
                  </span>
                )}
              </div>

              <div className="session-details">
                <div className="detail-card">
                  <div className="detail-header">
                    <span className="detail-icon">💻</span>
                    <h4>Device</h4>
                  </div>
                  <div className="detail-row">
                    <span className="detail-label">Type:</span>
                    <span className="detail-value">{session.device.type}</span>
                  </div>
                  <div className="detail-row">
                    <span className="detail-label">OS:</span>
                    <span className="detail-value">{session.device.os}</span>
                  </div>
                  <div className="detail-row">
                    <span className="detail-label">CPU:</span>
                    <span className="detail-value">{session.device.cpu}</span>
                  </div>
                </div>

                <div className="detail-card">
                  <div className="detail-header">
                    <span className="detail-icon">🌐</span>
                    <h4>Browser</h4>
                  </div>
                  <div className="detail-row">
                    <span className="detail-label">Name:</span>
                    <span className="detail-value">{session.browser.browser}</span>
                  </div>
                  <div className="detail-row">
                    <span className="detail-label">Version:</span>
                    <span className="detail-value">{session.browser.version}</span>
                  </div>
                  <div className="detail-row">
                    <span className="detail-label">Engine:</span>
                    <span className="detail-value">{session.browser.engine}</span>
                  </div>
                </div>

                {session.network && (
                  <div className="detail-card">
                    <div className="detail-header">
                      <span className="detail-icon">📡</span>
                      <h4>Network</h4>
                    </div>
                    <div className="detail-row">
                      <span className="detail-label">IP:</span>
                      <span className="detail-value">{session.network.ip}</span>
                    </div>
                    <div className="detail-row">
                      <span className="detail-label">ISP:</span>
                      <span className="detail-value">{session.network.org}</span>
                    </div>
                  </div>
                )}
              </div>

              {session.network && index === sessions.length - 1 && (
                <>
                  <div className="geolocation-info">
                    <div className="geo-header">
                      <span className="geo-icon">📍</span>
                      <h4>Geolocation Information</h4>
                      <span className="geo-badge">{session.network.country}</span>
                    </div>
                    <div className="geo-grid">
                      <div className="geo-item">
                        <span className="geo-label">City:</span>
                        <span className="geo-value">{session.network.city}, {session.network.region}</span>
                      </div>
                      <div className="geo-item">
                        <span className="geo-label">Postal Code:</span>
                        <span className="geo-value">{session.network.postal}</span>
                      </div>
                      <div className="geo-item">
                        <span className="geo-label">Coordinates:</span>
                        <span className="geo-value">{session.network.loc}</span>
                      </div>
                      <div className="geo-item">
                        <span className="geo-label">Timezone:</span>
                        <span className="geo-value">{session.network.timezone}</span>
                      </div>
                      <div className="geo-item">
                        <span className="geo-label">ISP:</span>
                        <span className="geo-value">{session.network.org}</span>
                      </div>
                      {session.network.hostname && (
                        <div className="geo-item full-width">
                          <span className="geo-label">Hostname:</span>
                          <span className="geo-value">{session.network.hostname}</span>
                        </div>
                      )}
                    </div>
                  </div>

                  <div className="security-info">
                    <div className="security-header">
                      <span className="security-icon">🔒</span>
                      <h4>Network Security Status</h4>
                    </div>
                    <div className="security-grid">
                      <div className="security-item">
                        <span className="security-label">No VPN</span>
                        <span className={`security-status ${!session.network.vpn ? 'pass' : 'fail'}`}>
                          {!session.network.vpn ? '✓' : '✗'}
                        </span>
                      </div>
                      <div className="security-item">
                        <span className="security-label">No Proxy</span>
                        <span className={`security-status ${!session.network.proxy ? 'pass' : 'fail'}`}>
                          {!session.network.proxy ? '✓' : '✗'}
                        </span>
                      </div>
                      <div className="security-item">
                        <span className="security-label">Not Hosting</span>
                        <span className={`security-status ${!session.network.hosting ? 'pass' : 'fail'}`}>
                          {!session.network.hosting ? '✓' : '✗'}
                        </span>
                      </div>
                      <div className="security-item">
                        <span className="security-label">No Relay</span>
                        <span className={`security-status ${!session.network.relay ? 'pass' : 'fail'}`}>
                          {!session.network.relay ? '✓' : '✗'}
                        </span>
                      </div>
                      <div className="security-item">
                        <span className="security-label">No Tor</span>
                        <span className={`security-status ${!session.network.tor ? 'pass' : 'fail'}`}>
                          {!session.network.tor ? '✓' : '✗'}
                        </span>
                      </div>
                    </div>
                  </div>
                </>
              )}

              <div className="session-status">
                <span className="status-indicator active">● Active Session</span>
              </div>
            </div>
          </div>
        ))}
      </div>

      <div className="log-summary">
        <div className="summary-item">
          <div className="summary-number">{sessions.length}</div>
          <div className="summary-label">Total Attempts</div>
        </div>
        <div className="summary-item">
          <div className="summary-number">{calculateCompletionTime()}</div>
          <div className="summary-label">Completion Time</div>
        </div>
        <div className="summary-item">
          <div className="summary-icon">{checkSameIP() ? '✓' : '✗'}</div>
          <div className="summary-label">Same IP Address</div>
        </div>
        <div className="summary-item">
          <div className="summary-flag">{sessions[sessions.length - 1].network?.country || 'Unknown'}</div>
          <div className="summary-label">{getLocation()}</div>
        </div>
      </div>
    </div>
  );
};

export default VerificationLog;